import { Button } from 'ui/button/button.module';

import { MessageWrapper } from 'components/messageWrapper/messageWrapper.module';
import { XPATH_BUTTONS } from 'consts/dataQaConst.js';
import { BUTTON_SIZE, BUTTON_TYPE, CARD_ACTION_TYPE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import {
	addClass,
	debounce,
	generate6DigitUUID,
	generateBtnOnClickFnc,
	hasClass,
	onEditResponse,
	removeClass,
	removeElement,
	setCustomTagHTML,
	writeToLs,
} from 'helpers/helpers';
import { phone } from 'icons/phone';
import { url } from 'icons/url';
import { urlBlack } from 'icons/urlBlack';
import { ENGT_CHAT } from 'js/bot.model';

import { smo } from './smo.model';
import { SmoTemplate } from './smo.template';

export default class Smo {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initComponents();
	}

	initData(propData = {}) {
		this.data = smo;
		this.setter(propData);
		this.data.id = propData.id || propData.message_id || `message-${generate6DigitUUID()}`;
		this.data.isOptionsDisabled = ENGT_CHAT.get('config.disableSmoOptions');
	}

	initWrapper() {
		this.html = new MessageWrapper({
			...this.data,
			message: this.data?.message?.data?.message || 'Message with options',
			innerHTML: SmoTemplate(this.data),
		});
	}

	initComponents() {
		const { message } = this.data;
		const { data = {}, enable_autofill, enable_search, buttons_alignment } = message || {};
		const { options = [] } = data;

		if (options.length) {
			const lastOption = { ...options[options.length - 1] };

			if (lastOption.payload === 'MULTI_SELECT_END') {
				this.smoWithMultiSelect(lastOption);
			} else if (enable_autofill) {
				this.smoAsDropdown();
			} else if (buttons_alignment === 'vertical') {
				this.verticalSMO();
			} else {
				this.horizontalSMO();
			}

			if (ENGT_CHAT.get('config.edit_user_input')) {
				const ref = this;
				const editButton = new Button({
					type: BUTTON_TYPE.MINI,
					label: ENGT_CHAT.get('config.default_edit_response')
						? ENGT_CHAT.get('config.default_edit_response')
						: ENGT_CHAT.get('GENERIC_STRINGS')?.['EDIT'] || 'Edit',
					id: `editBtn-${this.data.id}`,
					style: 'display:none;',
					applyOnHover: true,
					onClick() {
						const flowKey = ref.data.flow_key;
						const nodeKey = ref.data.node_key;
						if (flowKey && nodeKey) {
							this.style.display = 'none';
							onEditResponse(flowKey, nodeKey);
							ENGT_CHAT.set('editFlowId', '');
						} else {
							console.error('flow key and and node key are not set and hence not triggering edit flow');
						}
					},
					radius: 18,
				}).render();

				setCustomTagHTML(this.html.querySelector('edit-btn'), editButton);
				ENGT_CHAT.set('editFlowId', `editBtn-${this.data.id}`);
				ENGT_CHAT.set('isEditFlowUsed', false);
			} else {
				setCustomTagHTML(this.html.querySelector('edit-btn'), '');
			}
		} else if (enable_search) {
			this.smoAsDropdown();
		}
	}

	renderEditBtn() {
		const editBtn = this.html.querySelector(`#editBtn-${this.data.id}`);
		editBtn.style.display = 'block';
	}

	generateDropdownOption(index, option, input, dropdown, highlightText, ref) {
		const optionElement = document.createElement('div');
		const text = option.text.replaceAll(highlightText, `<b>${highlightText}</b>`);
		optionElement.setAttribute('aria-label', option.text);
		optionElement.setAttribute('tabindex', index);
		optionElement.innerHTML = `<span>${text}</span>`;
		const actionType = option.type?.toUpperCase?.();
		if (actionType === CARD_ACTION_TYPE.PHONE_NUMBER) {
			const icon = document.createElement('span');
			icon.className = 'engt-dropdown-start-enhancer';
			icon.innerHTML = phone();
			optionElement.appendChild(icon);
		} else if (actionType === CARD_ACTION_TYPE.WEB_URL) {
			const icon = document.createElement('span');
			icon.className = 'engt-dropdown-start-enhancer';
			icon.innerHTML = urlBlack();
			optionElement.appendChild(icon);
		}
		ref = this;
		optionElement.addEventListener('click', (e) => {
			input.value = e.target.innerText;
			if (ref.data.isOptionsDisabled) {
				input.disabled = true;
				input.style = '';
			}
			dropdown.style.display = 'none';
			generateBtnOnClickFnc({ ...option, title: option.text })();
		});
		if (index === 0) {
			optionElement.className = 'active';
		}
		dropdown.appendChild(optionElement);
	}

	showAutocompleteOptions(options) {
		this.dropdown.innerHTML = '';
		removeClass(this.dropdown, 'dropdown-loading');
		if (options?.length) {
			options.map((option, index) => this.generateDropdownOption(index, option, this.input, this.dropdown, ''));
		} else {
			this.dropdown.style.display = 'none';
			this.html.querySelector('input').style = '';
		}
	}

	horizontalSMO() {
		const { message } = this.data;
		const { data = {} } = message || {};
		const { options = [] } = data;
		const optionsDiv = document.createElement('div');

		const ref = this;
		optionsDiv.className = 'engt-msg-smo-options-row';
		options.length &&
			options.map((option, index) => {
				let { text } = option;
				const actionType = option.type?.toUpperCase?.();
				if (actionType === CARD_ACTION_TYPE.PHONE_NUMBER) {
					text = `<span class="engt-btn-start-enhancer">${phone()}</span>${text}`;
				} else if (actionType === CARD_ACTION_TYPE.WEB_URL) {
					text = `<span class="engt-btn-start-enhancer">${url()}</span>${text}`;
				}

				optionsDiv.appendChild(
					new Button({
						type: BUTTON_TYPE.BASE,
						size: BUTTON_SIZE.SMALL,
						label: text,
						disabled: ref.data.isOptionsDisabled && ref.data.isOldMessage,
						id: '',
						dataQa: XPATH_BUTTONS,
						onClick() {
							writeToLs(
								LS().TAB_MESSAGES,
								JSON.stringify({
									action: 'update_smo_msg',
									selectedValue: index,
									id: ref.data.id,
									tabId: ENGT_CHAT.get('socketId'),
								})
							);
							ref.data.isOptionsDisabled &&
								ref.html.querySelectorAll(`button:not(#editBtn-${ref.data.id})`).forEach((input) => {
									input.disabled = true;
								});
							generateBtnOnClickFnc({ ...option, title: option.text })();
						},
						applyOnHover: true,
						style: `width: unset;padding: 5px 10px;text-align: left; animation-delay: ${
							(index + 1) * 0.1
						}s;`,
						radius: 12,
					}).render()
				);
			});

		setCustomTagHTML(this.html.querySelector('smo-options'), optionsDiv);
	}

	verticalSMO() {
		const { message } = this.data;
		const { data = {} } = message || {};
		const { options = [] } = data;
		const optionsDiv = document.createElement('div');

		const ref = this;
		optionsDiv.className = 'engt-msg-smo-options-column';
		options.map((option, index) => {
			let { text } = option;
			const actionType = option.type?.toUpperCase?.();
			if (actionType === CARD_ACTION_TYPE.PHONE_NUMBER) {
				text = `<span class="engt-btn-start-enhancer">${phone()}</span>${text}`;
			} else if (actionType === CARD_ACTION_TYPE.WEB_URL) {
				text = `<span class="engt-btn-start-enhancer">${url()}</span>${text}`;
			}

			optionsDiv.appendChild(
				new Button({
					type: BUTTON_TYPE.BASE,
					size: BUTTON_SIZE.SMALL,
					label: text,
					disabled: ref.data.isOptionsDisabled && ref.data.isOldMessage,
					onClick() {
						writeToLs(
							LS().TAB_MESSAGES,
							JSON.stringify({
								action: 'update_smo_msg',
								selectedValue: index,
								id: ref.data.id,
								tabId: ENGT_CHAT.get('socketId'),
							})
						);
						ref.data.isOptionsDisabled &&
							ref.html.querySelectorAll(`button:not(#editBtn-${ref.data.id})`).forEach((input) => {
								input.disabled = true;
							});
						generateBtnOnClickFnc({ ...option, title: option.text }, 'skip')();
					},
					applyOnHover: true,
					style: `text-align: left; animation-delay: ${(index + 1) * 0.1}s;`,
				}).render()
			);
		});

		setCustomTagHTML(this.html.querySelector('smo-options'), optionsDiv);
	}

	smoWithMultiSelect(lastOption) {
		const { message } = this.data;
		const { data = {} } = message || {};
		const { options = [] } = data;
		const optionsDiv = document.createElement('div');

		const ref = this;
		this.selectedOptions = {};

		const disableButtons = function () {
			const isOptionsSelected = Object.keys(ref.selectedOptions)?.length > 0;
			const submitBtn = ref.html.querySelector('#smoSubmitBtn');
			submitBtn.disabled = !isOptionsSelected;
			if (isOptionsSelected) {
				removeClass(submitBtn, 'engt-btn-disabled');
			} else {
				addClass(submitBtn, 'engt-btn-disabled');
			}
		};

		const disableOptionsAndButtons = function () {
			ref.html.querySelectorAll('input').forEach((input) => {
				input.disabled = true;
			});
			ref.html.querySelectorAll(`button:not(#editBtn-${ref.data.id})`).forEach((input) => {
				input.disabled = true;
			});
		};

		const toggleOption = function (option, isChecked) {
			if (isChecked) {
				ref.selectedOptions[option.payload] = {
					...option,
				};
			} else {
				delete ref.selectedOptions[option.payload];
			}
		};

		const onChange = function (option) {
			toggleOption(option, this.checked);
			disableButtons();
		};

		optionsDiv.className = 'engt-msg-smo-options-column';
		options.map((option, index) => {
			if (index + 1 === options.length) {
				optionsDiv.appendChild(
					new Button({
						type: BUTTON_TYPE.PRIMARY,
						size: BUTTON_SIZE.MEDIUM,
						label: option.text,
						id: 'smoSubmitBtn',
						disabled: true,
						className: 'engt-btn-disabled',
						onClick() {
							const dataPacket = {
								prefix: '',
								text: [],
								payload: [],
							};

							Object.values(ref.selectedOptions)
								.reverse()
								.map((selectedOpt) => {
									const [prefix, payload] = selectedOpt.payload.split('=');
									dataPacket.prefix = `${prefix}=`;
									dataPacket.text.push(selectedOpt.text);
									dataPacket.payload.push(payload);
								});

							generateBtnOnClickFnc({
								type: 'postback',
								text: dataPacket.text.reverse().join(','),
								payload: dataPacket.prefix + dataPacket.payload.reverse().join(','),
								title: dataPacket.text.join(','),
							})();
							if (ref.data.isOptionsDisabled) {
								disableOptionsAndButtons();
							}

							this.disabled = true;
							addClass(this, 'engt-btn-disabled');
						},
						applyOnHover: true,
						style: `margin-top: 20px; padding: 8px 6.8px; text-transform: capitalize; animation-delay: ${
							(index + 1) * 0.1
						}s`,
						radius: 18,
					}).render()
				);
			} else {
				const checkBoxDiv = document.createElement('div');
				checkBoxDiv.className = 'engt-msg-smo-options-checkbox';

				const inputCheckbox = document.createElement('input');
				inputCheckbox.type = 'checkbox';
				inputCheckbox.disabled = ref.data.isOptionsDisabled && ref.data.isOldMessage;
				inputCheckbox.addEventListener('change', function () {
					onChange.call(this, option);
				});

				checkBoxDiv.appendChild(inputCheckbox);
				checkBoxDiv.appendChild(
					new Button({
						type: BUTTON_TYPE.BASE,
						size: BUTTON_SIZE.SMALL,
						label: option.text,
						disabled: ref.data.isOptionsDisabled && ref.data.isOldMessage,
						onClick() {
							const isChecked = this.parentNode.querySelector('input').checked;
							this.parentNode.querySelector('input').checked = !isChecked;

							toggleOption(option, !isChecked);
							disableButtons();
						},
						applyOnHover: true,
						style: `text-align: left; padding: 8px 6.8px; animation-delay: ${(index + 1) * 0.1}s`,
					}).render()
				);

				optionsDiv.appendChild(checkBoxDiv);
			}
		});

		this.html.querySelector('.engt-msg-smo').classList.add('engt-msg-smo-lengthy');
		setCustomTagHTML(this.html.querySelector('smo-options'), optionsDiv);
		ref.data.isOptionsDisabled && this.data.isOldMessage && disableButtons();
	}

	smoAsDropdown() {
		const { message } = this.data;
		const { data = {}, enable_search } = message || {};
		const { options = [] } = data;
		const ref = this;
		const optionsDiv = document.createElement('div');
		const dropdown = document.createElement('div');
		const input = document.createElement('input');
		const arrow = document.createElement('div');

		function opneDropdownOnFocus() {
			if (input.value) {
				onInputChange({}, input.value);
				removeElement(ref.html.querySelector('.engt-loader'));
			}
			openDropdown();
		}

		function openDropdown() {
			dropdown.style.display = 'block';
			arrow.className = 'up-arrow';
			if (dropdown?.children?.length) {
				input.style.borderBottomLeftRadius = '0px';
				input.style.borderBottomRightRadius = '0px';
				dropdown.scrollIntoView({ block: 'center', inline: 'nearest' });
			} else {
				input.style = '';
				dropdown.style.display = 'none';
			}
		}

		function closeDropdown(forceClose = false) {
			setTimeout(
				() => {
					input.style = '';
					dropdown.style.display = 'none';
					arrow.className = 'down-arrow';
					removeClass(dropdown, 'dropdown-loading');
				},
				forceClose === true ? 0 : 300
			);
		}

		function onInputChange(e, initialInput = '') {
			const text = e?.target?.value?.toLowerCase?.() || initialInput || '';
			if (ref.data.isOptionsDisabled && ref.data.isOldMessage) {
				return;
			}

			if (options.length !== 0) {
				removeClass(dropdown, 'dropdown-loading');
				dropdown.innerHTML = '';
				let filteredOptions = options;
				if (options.length > 400) {
					filteredOptions = options.slice(0, 400);
				}
				filteredOptions = filteredOptions
					.filter(
						(option) =>
							(!ref.data.isOptionsDisabled && !e?.target?.value) ||
							!text ||
							(option?.text && option.text.toLowerCase?.()?.indexOf?.(text) > -1)
					)
					.map((option, index) => ref.generateDropdownOption(index, option, input, dropdown, text));

				if (!filteredOptions.length) {
					closeDropdown(true);
				} else {
					openDropdown();
				}
			} else if (enable_search) {
				ref.dropdown = dropdown;
				ref.input = input;
				ref.showAutocompleteOptions = ref.showAutocompleteOptions.bind(ref);
				ENGT_CHAT.set('activeSmoDropdown', ref);
				ENGT_CHAT.get('sendMessage')('autocomplete', {
					message: {
						type: 'autocomplete',
						text,
					},
					tab_msg_action: 'skip',
					skipAppendToDom: true,
				});
				openDropdown();
			}
		}

		function onInputKeydown(e) {
			const keyCode = e.which ? e.which : e.keyCode;
			keyCode === 13 && !e.shiftKey && selectActiveOption();
			keyCode === 40 && highlightDown();
			keyCode === 38 && highlightUp();
		}

		function highlightDown() {
			const dropdownItems = dropdown.children;
			for (let index = 0; index < dropdownItems.length; index++) {
				const element = dropdownItems[index];

				if (hasClass(element, 'active')) {
					element.className = '';
					if (index < dropdownItems.length - 1) {
						dropdownItems[index + 1].className = 'active';
					} else {
						dropdownItems[0].className = 'active';
					}
					return;
				}
			}
		}

		function highlightUp() {
			const dropdownItems = dropdown.children;
			for (let index = 0; index < dropdownItems.length; index++) {
				const element = dropdownItems[index];

				if (hasClass(element, 'active')) {
					element.className = '';
					if (index > 0) {
						dropdownItems[index - 1].className = 'active';
					} else {
						dropdownItems[dropdownItems.length - 1].className = 'active';
					}
					return;
				}
			}
		}

		function selectActiveOption() {
			const activeElement = dropdown?.querySelector?.('.active');
			if (activeElement) {
				activeElement.click();
			}
		}

		function showLoader() {
			if (!hasClass(dropdown, 'dropdown-loading')) {
				const loader = document.createElement('div');
				loader.className = 'engt-loader';
				dropdown.innerHTML = '';
				dropdown.appendChild(loader);
				addClass(dropdown, 'dropdown-loading');
			}
		}

		const inputChange = debounce(onInputChange, 250);

		const debouncedInputChange = (e) => {
			if (enable_search) {
				openDropdown();
				showLoader();
			}
			inputChange?.(e);
		};

		optionsDiv.className = 'engt-msg-smo-dropdown';
		dropdown.className = 'dropdown';
		input.autocomplete = 'on';
		arrow.className = 'down-arrow';
		input.setAttribute(
			'placeholder',
			ENGT_CHAT.get('GENERIC_STRINGS')?.['SETTINGS.PLACEHOLDER.SELECT'] || 'Enter/Select'
		);
		input.disabled = ref.data.isOptionsDisabled && this.data.isOldMessage;
		input.addEventListener('input', debouncedInputChange);
		input.addEventListener('focus', opneDropdownOnFocus);
		input.addEventListener('focusout', closeDropdown);
		input.addEventListener('keydown', onInputKeydown);
		optionsDiv.appendChild(input);
		optionsDiv.appendChild(arrow);
		optionsDiv.appendChild(dropdown);
		onInputChange();
		closeDropdown(true);

		this.html.querySelector('.engt-msg-smo').classList.add('engt-msg-smo-lengthy');
		setCustomTagHTML(this.html.querySelector('smo-options'), optionsDiv);
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	render() {
		return this.html;
	}
}
